import { useState } from 'react';

import styled from '@emotion/styled';

import { MetadataDetails } from '../MetadataDetails';
import { Modal, ModalHeader, ModalBody } from '../Modal';

import type { MetadataDetailsProps } from '../MetadataDetails';
import type { ReviewDetailsProps } from './ReviewDetails.types';

// Shared props applied to all MetadataDetails instances - EXACT from LatestReviewDetails.tsx
const METADATA_PROPS: Pick<MetadataDetailsProps, 'layout' | 'valueFontSize' | 'labelFontSize'> = {
  layout: 'stacked',
  valueFontSize: 'xs',
  labelFontSize: 'xs',
};

// EXACT styled components from LatestReviewDetails.tsx

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const TextPreviewValue = styled.span`
  ${({ theme }) => `
    font-size: ${theme.typography.fontSize.xs};
    color: ${theme.colors.text.primary};
    display: flex;
    align-items: center;
    gap: ${theme.spacing[2]};
  `}
`;

const TextPreviewEllipsis = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
`;

const ShowMoreButton = styled.button`
  ${({ theme }) => `
    background: none;
    border: none;
    color: ${theme.colors.primary};
    font-size: ${theme.typography.fontSize.xs};
    cursor: pointer;
    padding: 0;
    text-decoration: underline;
    
    &:hover {
      color: ${theme.colors.highlight};
    }
  `}
`;

const TextModalContent = styled.div`
  ${({ theme }) => `
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.text.primary};
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 400px;
    overflow-y: auto;
    line-height: 1.6;
  `}
`;

const FileListValue = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const FileListItem = styled.span`
  ${({ theme }) => `
    font-size: ${theme.typography.fontSize.xs};
    color: ${theme.colors.text.primary};
  `}
`;

/**
 * Get display text for review type - EXACT from LatestReviewDetails.tsx
 */
function getReviewTypeDisplay(
  type: 'file' | 'text' | 'weburl',
  hasPageUrl: boolean,
  isMultiFile = false
): string {
  if (isMultiFile) return 'Multi-File';
  if (type === 'text') return 'Text';
  if (hasPageUrl || type === 'weburl') return 'Web URL';
  return 'File';
}

/**
 * Formats a timestamp for display with browser timezone - EXACT from LatestReviewDetails.tsx
 */
function formatTimestampWithTimezone(timestamp: string | Date): string {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    date.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }) + ` (${timeZone})`
  );
}

/**
 * ReviewDetails component - Displays review metadata using MetadataDetails
 *
 * EXACT copy of LatestReviewDetails from web-app-risk-reviews
 *
 * Supports both single-file and multi-file review results.
 *
 * Displays:
 * - Review Type (File, Text, Web URL, Multi-File)
 * - File/Text/Page content
 * - Connected App info with entity hierarchy
 * - Filter categories with values
 * - Review ID
 * - Review Time with timezone
 *
 * @example
 * ```tsx
 * // Single-file review
 * <ReviewDetails
 *   review={latestReview}
 *   getFilterDisplayText={getFilterDisplayText}
 * />
 *
 * // Multi-file review
 * <ReviewDetails
 *   multiFileResult={multiFileResults}
 *   getFilterDisplayText={getFilterDisplayText}
 * />
 * ```
 */
export const ReviewDetails: React.FC<ReviewDetailsProps> = ({
  review,
  multiFileResult,
  getFilterDisplayText,
  className,
  dataTestId,
  dataId,
  style,
}) => {
  const [showTextModal, setShowTextModal] = useState(false);

  // Handle multi-file results
  if (multiFileResult) {
    const selectedFilters = multiFileResult.selectedFilters || [];
    const connectedAppInfo = multiFileResult.connectedAppInfo;
    const fileNames = multiFileResult.results.map((r) => r.fileName);

    return (
      <DetailsContainer
        className={className}
        data-testid={dataTestId}
        data-id={dataId}
        style={style}
      >
        <MetadataDetails {...METADATA_PROPS} name="REVIEW TYPE" value="Multi-File" />

        <MetadataDetails
          {...METADATA_PROPS}
          name="FILES"
          type="Custom"
          component={
            <FileListValue>
              {fileNames.map((name, index) => (
                <FileListItem key={index}>{name}</FileListItem>
              ))}
            </FileListValue>
          }
        />

        <MetadataDetails
          {...METADATA_PROPS}
          name="TOTAL FILES"
          value={`${multiFileResult.results.length} files`}
        />

        <MetadataDetails
          {...METADATA_PROPS}
          name="TOTAL FINDINGS"
          value={`${multiFileResult.totalFindingsCount} risks`}
        />

        {connectedAppInfo && (
          <>
            <MetadataDetails
              {...METADATA_PROPS}
              name="CONNECTED APP"
              value={connectedAppInfo.connectorName}
            />

            {connectedAppInfo.entities.map((entity) => (
              <MetadataDetails
                {...METADATA_PROPS}
                key={`${entity.type}-${entity.id}`}
                name={`${connectedAppInfo.connectorName.toUpperCase()} ${entity.type.toUpperCase()}`}
                value={entity.name || entity.id}
              />
            ))}
          </>
        )}

        {selectedFilters.map((filter) => (
          <MetadataDetails
            {...METADATA_PROPS}
            key={filter.categoryId}
            name={`CATEGORY - ${filter.categoryName}`}
            value={getFilterDisplayText(filter)}
          />
        ))}

        <MetadataDetails
          {...METADATA_PROPS}
          name="REVIEW TIME"
          value={formatTimestampWithTimezone(multiFileResult.reviewedAt)}
        />
      </DetailsContainer>
    );
  }

  // Handle empty/null review case
  if (!review) {
    return (
      <DetailsContainer
        className={className}
        data-testid={dataTestId}
        data-id={dataId}
        style={style}
      >
        <MetadataDetails {...METADATA_PROPS} name="STATUS" value="No review data available" />
      </DetailsContainer>
    );
  }

  // Handle single-file review
  const selectedFilters = review.selectedFilters || [];
  const connectedAppInfo = review.connectedAppInfo;
  const reviewType = getReviewTypeDisplay(review.type, !!review.pageUrl);

  // Determine what to show for FILE / PAGE / TEXT field
  const getContentDisplay = () => {
    if (review.type === 'text' && review.inputText) {
      const text = review.inputText;
      const maxLength = 50;
      if (text.length > maxLength) {
        return (
          <TextPreviewValue>
            <TextPreviewEllipsis>{text.substring(0, maxLength)}...</TextPreviewEllipsis>
            <ShowMoreButton onClick={() => setShowTextModal(true)}>Show more</ShowMoreButton>
          </TextPreviewValue>
        );
      }
      return text;
    }
    if (review.pageUrl) {
      return review.pageUrl;
    }
    if (review.fileName) {
      return review.fileName;
    }
    return 'N/A';
  };

  // Determine the label for FILE / PAGE / TEXT
  const getContentLabel = (): string => {
    if (review.type === 'text') return 'TEXT';
    if (review.pageUrl) return 'PAGE';
    return 'FILE';
  };

  return (
    <>
      <DetailsContainer
        className={className}
        data-testid={dataTestId}
        data-id={dataId}
        style={style}
      >
        <MetadataDetails {...METADATA_PROPS} name="REVIEW TYPE" value={reviewType} />

        <MetadataDetails
          {...METADATA_PROPS}
          name={getContentLabel()}
          type={
            review.type === 'text' && review.inputText && review.inputText.length > 50
              ? 'Custom'
              : 'Normal'
          }
          value={typeof getContentDisplay() === 'string' ? (getContentDisplay() as string) : undefined}
          component={typeof getContentDisplay() !== 'string' ? getContentDisplay() : undefined}
        />

        {connectedAppInfo && (
          <>
            <MetadataDetails
              {...METADATA_PROPS}
              name="CONNECTED APP"
              value={connectedAppInfo.connectorName}
            />

            {connectedAppInfo.entities.map((entity) => (
              <MetadataDetails
                {...METADATA_PROPS}
                key={`${entity.type}-${entity.id}`}
                name={`${connectedAppInfo.connectorName.toUpperCase()} ${entity.type.toUpperCase()}`}
                value={entity.name || entity.id}
              />
            ))}
          </>
        )}

        {selectedFilters.map((filter) => (
          <MetadataDetails
            {...METADATA_PROPS}
            key={filter.categoryId}
            name={`CATEGORY - ${filter.categoryName}`}
            value={getFilterDisplayText(filter)}
          />
        ))}

        <MetadataDetails {...METADATA_PROPS} name="REVIEW ID" value={review.reviewId} />

        <MetadataDetails
          {...METADATA_PROPS}
          name="REVIEW TIME"
          value={formatTimestampWithTimezone(review.timestamp)}
        />
      </DetailsContainer>

      {/* Text Content Modal */}
      <Modal isOpen={showTextModal} onClose={() => setShowTextModal(false)} size="md">
        <ModalHeader>Review Text Content</ModalHeader>
        <ModalBody>
          <TextModalContent>{review.inputText || ''}</TextModalContent>
        </ModalBody>
      </Modal>
    </>
  );
};

ReviewDetails.displayName = 'ReviewDetails';

export default ReviewDetails;
